import axios from "axios";
import * as rax from 'retry-axios';
import {getBrowserId} from "./store/utils";


/**
 * the api servers a development build can talk to; the choice lives in localStorage as
 * 'apiTarget' and survives reloads.  A production build always uses VUE_APP_API_URL.
 */
const apiTargets = {
  // the machine serving the page, so the dev server works from another one on the LAN
  local: {label: 'local', title: 'LOCAL', baseURL: `http://${window.location.hostname}:3000`},
  staging: {label: 'staging', title: 'STAGING', baseURL: 'https://api-staging.fedfedfed.com/'},
  production: {label: 'live', title: 'LIVE', baseURL: 'https://api.fedfedfed.com/'},
};
const defaultBaseURL = process.env.VUE_APP_API_URL || apiTargets.local.baseURL;
const defaultSocketURL = process.env.VUE_APP_SOCKET_URL || defaultBaseURL;
const isProductionBuild = process.env.VUE_APP_FED_ENV === 'production';
let url;

function targetOfBaseURL(baseURL) {
  const stripSlash = value => value.replace(/\/$/, '');
  return Object.keys(apiTargets).find(name => stripSlash(apiTargets[name].baseURL) === stripSlash(baseURL)) || null;
}

/** the target chosen in this browser, if any; the old 'useProduction' flag is carried over */
function storedApiTarget() {
  const stored = localStorage.getItem('apiTarget');
  if (apiTargets[stored]) {
    return stored;
  }
  if (localStorage.getItem('useProduction') === 'true') {
    localStorage.removeItem('useProduction');
    localStorage.setItem('apiTarget', 'production');
    return 'production';
  }
  return null;
}

function createApi() {
  let baseURL = defaultBaseURL;
  let apiTarget = targetOfBaseURL(defaultBaseURL);
  const chosen = isProductionBuild ? null : storedApiTarget();
  if (chosen === 'production'
    && !confirm('you are using a test build on live production data.  be aware that all data changes will impact real users.  click cancel to go back to the test api server')) {
    localStorage.removeItem('apiTarget');
    location.reload();
  } else if (chosen) {
    apiTarget = chosen;
    baseURL = apiTargets[chosen].baseURL;
  }

  const raxConfig = {
    // You can detect when a retry is happening, and figure out how many
    // retry attempts have been made
    onRetryAttempt: err => {
      const cfg = rax.getConfig(err);
      console.log(`Retry attempt #${cfg.currentRetryAttempt} of ${cfg.retry}.  rax.shouldRetryRequest(err)=${rax.shouldRetryRequest(err)}`);
    },
    // shouldRetry(err) {
    //   console.log('should retry?', rax.shouldRetryRequest(err))
    //   return rax.shouldRetryRequest(err);
    // },
    statusCodesToRetry: [[399, 401], [500, 599]],
    retry: 10,

  };
  url = baseURL;
  const api = axios.create({
    baseURL: baseURL,
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Authorization': 'bearer ' + localStorage.getItem('token'),
      'fedbrowserid': getBrowserId(),
    },
    raxConfig
  });

  rax.attach(api);


  let lastWarning = 0;
  // eslint-disable-next-line no-constant-condition
  //if (false)
  api.interceptors.response.use(
    (config) => {
      return config;
    }, (error) => {
      if (!error.response) {
        console.log('api error, no response', error);
        alert('There seems to be a problem connecting to the server.  \n\nIf your internet connection is working and this message persists, please contact the admin.');
      } else {
        console.log('api error', error.response.status);
        if (error.response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          if (Date.now() - lastWarning > (30 * 1000)) {
            lastWarning = Date.now();
            alert("Server authorization required.  If you are signed in, it might be your role isn't allowed to do this operation.");
          }
          location.reload();
        }
      }
      if (api.errorHandler) {
        try {
          api.errorHandler(error);
        } catch (e) {
          console.error('failure in error handler', e);
        }
      } else {
        console.log('api error', error);
      }
      return Promise.reject(error);
    });


  api.setErrorHandler = (handler) => {
    api.errorHandler = handler;
  }
  api.showError = ({message}) => {
    api.errorHandler && api.errorHandler({message});
  }

  api.setToken = (token) => {
    api.defaults.headers.Authorization = 'bearer ' + token;
  }
  api.apiTarget = apiTarget;
  api.isProductionApi = apiTarget === 'production';
  return api;
}

/** switch this browser to another api server: the login belongs to the old one, so it is dropped */
function useApiTarget(target) {
  if (!apiTargets[target]) {
    throw new Error(`unknown api target ${target}`);
  }
  localStorage.setItem('apiTarget', target);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  location.reload();
}

const axiosInstance = createApi();
const socketUrl = defaultSocketURL;
export {url, socketUrl};
export default {
  isProductionBuild,
  apiTargets,
  useApiTarget,
  ...axiosInstance,
};
